/* Copyright © 2026 YOUR GROWTH 365 (Jonas Brössel) — Proprietary. See LICENSE in repo root. */
/* =============================================================================
   VIEWS / ADMIN / SCHEDULE-PUBLISH — Dienstplan veröffentlichen (S264, Cut C)
   =============================================================================
   Jonas-Entscheide 7/8 (Spec § 1): Veröffentlichen = Mail an alle Teilnehmer
   mit der ganzen Woche + PDF-Anhang; jede Änderung danach = erneut
   veröffentlichen = neue Mail + PDF an alle, Version im Betreff.

   PDF-Pfad wie der Z-Bon (Spec § 7, keine neue Technik):
     buildStandaloneScheduleHtml(overview)   — A4 quer, Brand-Kopf, Raster
     → inlineBrandLogoForEmail                — Logo als Data-URL (html2canvas)
     → receiptHtmlToPdfBase64(html, {widthMm:297, heightMm:210})
     → schedulePdfs.uploadPdfBase64({rid}/{week_id}-v{n}.pdf)
     → schedule_publish(week_id, pdf_path, session)  — v+1, Outbox je Teilnehmer.

   Das Standalone-HTML trägt ein eigenes <style> mit body-Regeln. Es darf NIE
   per innerHTML ins Cockpit (S230 Style-Leak) — Vorschau und PDF-Erzeugung
   laufen in einem iframe.
============================================================================ */

import { htmlEscape, showToast } from '../../utils/helpers.js';
import { receiptHtmlToPdfBase64 } from '../../utils/receipt-pdf.js';
import { inlineBrandLogoForEmail, getBrandLogoSrc } from '../../core/branding.js';
import {
  SHIFT_CODES, SHIFT_LABELS, isoWeekNumber, formatDayShort, formatDayLong, formatWeekRange, weekDays,
} from '../../core/schedule-shifts.js';

function _settings() {
  try {
    const st = window.App && window.App.storage && window.App.storage._getInternalState
      ? window.App.storage._getInternalState() : null;
    return (st && st.settings) || {};
  } catch (_) { return {}; }
}

/* -------------------------------------------------------------------------- */
/* Pure                                                                        */
/* -------------------------------------------------------------------------- */

/** Dateiname des Plan-PDFs (Spiegel von schedule_publish v_fn). */
export function schedulePdfFilename(weekStart, version) {
  return `Dienstplan-KW${isoWeekNumber(weekStart)}-v${Number(version) || 1}.pdf`;
}

/** Empfänger einer Veröffentlichung: Schalter-User (aktiv) ∪ Anfragen der Woche. */
export function publishRecipients(participants) {
  return (Array.isArray(participants) ? participants : [])
    .filter(p => p && ((p.participant && p.active !== false) || p.request_id));
}

export function isEmptyPlan(assignments) {
  return !Array.isArray(assignments) || assignments.length === 0;
}

function _firstName(name) {
  return String(name || '').trim().split(/\s+/)[0] || '—';
}

function _fmtStamp(d) {
  const dt = d instanceof Date ? d : new Date(d || Date.now());
  if (isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' ' + dt.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

/**
 * Das Wochenraster als eigenständiges HTML-Dokument (A4 quer). Rein — nimmt
 * alles aus `overview` (schedule_week_overview) und `opts`:
 *   opts.version   Versionsnummer für die Fußzeile (Default: week.published_version || 1)
 *   opts.brandName Anzeigename (Default: settings.restaurantName)
 *   opts.logoSrc   Logo-URL/Pfad (Default: getBrandLogoSrc())
 *   opts.stamp     Datum für „Stand" (Default: jetzt)
 */
export function buildStandaloneScheduleHtml(overview, opts) {
  const o = overview || {};
  const op = opts || {};
  const days = (Array.isArray(o.days) && o.days.length === 7) ? o.days : weekDays(o.week_start);
  const parts = Array.isArray(o.participants) ? o.participants : [];
  const byId = new Map(parts.map(p => [p.user_id, p]));
  const asg = Array.isArray(o.assignments) ? o.assignments : [];
  const version = Number(op.version) || (o.week && o.week.published_version) || 1;
  const brandName = String(op.brandName || _settings().restaurantName || 'Grill & Green');
  const logoSrc = op.logoSrc || getBrandLogoSrc();
  const weekNo = o.week_no || isoWeekNumber(o.week_start);
  const range = formatWeekRange(o.week_start);
  const stamp = _fmtStamp(op.stamp || new Date());

  const cell = (day, shift) => {
    const people = asg.filter(a => a && a.day === day && a.shift === shift)
      .map(a => byId.get(a.user_id) || { name: '?', initials: '??' })
      .sort((a, b) => String(a.name).localeCompare(String(b.name), 'de'));
    if (!people.length) return '<span class="dp-empty">–</span>';
    return people.map(p => `<span class="dp-chip"><b>${htmlEscape(p.initials || '??')}</b> ${htmlEscape(_firstName(p.name))}</span>`).join('');
  };
  const rows = days.map(d => `
      <tr>
        <th scope="row"><span class="dp-dow">${htmlEscape(formatDayLong(d).split(',')[0])}</span><span class="dp-date">${htmlEscape(formatDayShort(d).slice(3))}</span></th>
        ${SHIFT_CODES.map(s => `<td>${cell(d, s)}</td>`).join('')}
      </tr>`).join('');
  const logoTag = logoSrc ? `<img class="dp-logo" src="${htmlEscape(logoSrc)}" alt="">` : '';

  return `<!DOCTYPE html>
<html lang="de"><head><meta charset="utf-8">
<title>Dienstplan KW ${htmlEscape(String(weekNo))} · ${htmlEscape(brandName)}</title>
<style>
  @page { size: A4 landscape; margin: 10mm; }
  body { font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; background: #f4f1ea; margin: 0; padding: 18px; color: #1a2622; color-scheme: light; }
  .dp { background: #fff; width: 1085px; margin: 0 auto; padding: 22px 28px 18px; border-radius: 12px; box-shadow: 0 6px 24px rgba(0,0,0,.08); box-sizing: border-box; }
  .dp-head { display: flex; align-items: center; justify-content: space-between; gap: 16px; border-bottom: 2px solid #1a2622; padding-bottom: 12px; margin-bottom: 14px; }
  .dp-brand { display: flex; align-items: center; gap: 12px; }
  .dp-logo { max-width: 70px; max-height: 52px; width: auto; height: auto; object-fit: contain; display: block; }
  .dp-brand-name { font-family: cursive; font-size: 1.7rem; color: #2f6b3a; }
  .dp-title { text-align: right; }
  .dp-title h1 { margin: 0; font-size: 1.35rem; }
  .dp-title p { margin: 4px 0 0; color: #5b6660; font-size: .95rem; }
  table { width: 100%; border-collapse: collapse; table-layout: fixed; }
  thead th { text-align: left; font-size: .78rem; text-transform: uppercase; letter-spacing: .05em; color: #5b6660; padding: 6px 8px; border-bottom: 1px solid #c9c1ad; }
  thead th:first-child { width: 120px; }
  tbody th { text-align: left; padding: 8px; border-bottom: 1px solid #e0d9c7; vertical-align: top; }
  tbody td { padding: 8px; border-bottom: 1px solid #e0d9c7; vertical-align: top; }
  tbody tr:nth-child(6) th, tbody tr:nth-child(6) td, tbody tr:nth-child(7) th, tbody tr:nth-child(7) td { background: #f7f4ec; }
  .dp-dow { display: block; font-weight: 700; }
  .dp-date { display: block; color: #5b6660; font-size: .85rem; }
  .dp-chip { display: inline-block; margin: 2px 6px 2px 0; padding: 3px 9px; border-radius: 999px; background: #dfecdf; color: #14361b; font-size: .92rem; white-space: nowrap; }
  .dp-chip b { font-size: .8rem; opacity: .85; }
  .dp-empty { color: #9aa39d; }
  .dp-foot { display: flex; justify-content: space-between; margin-top: 12px; font-size: .78rem; color: #5b6660; }
</style></head>
<body>
  <div class="dp">
    <div class="dp-head">
      <div class="dp-brand">${logoTag}<div class="dp-brand-name">${htmlEscape(brandName)}</div></div>
      <div class="dp-title">
        <h1>Dienstplan KW ${htmlEscape(String(weekNo))}</h1>
        <p>${htmlEscape(range)}</p>
      </div>
    </div>
    <table>
      <thead><tr><th>Tag</th>${SHIFT_CODES.map(s => `<th>${htmlEscape(SHIFT_LABELS[s])}</th>`).join('')}</tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <div class="dp-foot">
      <span>Stand v${htmlEscape(String(version))} · ${htmlEscape(stamp)}</span>
      <span>Änderungen werden mit einer neuen Version an alle gemailt.</span>
    </div>
  </div>
</body></html>`;
}

/* -------------------------------------------------------------------------- */
/* Aktionen                                                                    */
/* -------------------------------------------------------------------------- */

async function _rpc(fn, args) {
  const supa = window.App && window.App.supabase;
  if (!supa) throw new Error('Verbindung nicht bereit.');
  const { data, error } = await supa.rpc(fn, args);
  if (error) throw error;
  return data;
}

/**
 * Der ganze Veröffentlichen-Fluss. `onProgress(text)` meldet die Schritte
 * (PDF bauen → hochladen → senden). Liefert die RPC-Antwort
 * {version, recipients, mails_enqueued, kind_enabled, pdf_path, filename}.
 * Wirft bei Fehlern — der Aufrufer zeigt den Toast.
 */
export async function publishSchedule(overview, opts) {
  const o = overview || {};
  const week = o.week;
  if (!week || !week.id) throw new Error('SCHEDULE_WEEK_UNKNOWN: Für diese Woche gibt es noch keine Abfrage.');
  const onProgress = (opts && typeof opts.onProgress === 'function') ? opts.onProgress : () => {};
  const sid = opts && opts.sessionId;
  const sp = window.App && window.App.storage && window.App.storage.schedulePdfs;
  if (!sp || typeof sp.uploadPdfBase64 !== 'function') throw new Error('Dienstplan-Bucket nicht verfügbar.');

  const nextVersion = (Number(week.published_version) || 0) + 1;

  onProgress('PDF wird gebaut …');
  const html = buildStandaloneScheduleHtml(o, { version: nextVersion });
  const withLogo = await inlineBrandLogoForEmail(html);
  const base64 = await receiptHtmlToPdfBase64(withLogo, {
    widthMm: 297, heightMm: 210, renderWidthPx: 1123, scale: 2, quality: 0.85,
  });
  if (!base64) throw new Error('PDF konnte nicht erzeugt werden.');

  onProgress('PDF wird hochgeladen …');
  const path = sp.objectPath(week.id, nextVersion);
  await sp.uploadPdfBase64(path, base64);

  onProgress('Plan wird gesendet …');
  const res = await _rpc('schedule_publish', {
    p_week_id: week.id, p_pdf_path: path, p_operator_session_id: sid || null,
  });
  return res;
}

let _previewModal = null;
let _previewFrame = null;
let _previewResize = null;
let _previewKey = null;

/**
 * K4 (S265) — Skalierungsfaktor der Bildschirm-Vorschau: die A4-quer-Seite
 * (Standalone-HTML, ~1103–1121 px) wird auf die iframe-Breite gebracht. Nie > 1
 * (kein Aufblasen), bei unbrauchbaren Maßen 1 (unskaliert). Pure.
 */
export function previewScaleFactor(frameWidthPx, pageWidthPx) {
  const f = Number(frameWidthPx), p = Number(pageWidthPx);
  if (!Number.isFinite(f) || !Number.isFinite(p) || f <= 0 || p <= 0) return 1;
  return Math.min(1, f / p);
}

/**
 * K4 — Skalierung NUR am Bildschirm: transform auf dem body des srcdoc-Dokuments,
 * Breite der unskalierten Seite, html-Höhe auf das skalierte Maß (sonst bleibt
 * unten Leerraum). Der Druckpfad setzt das per @media print zurück; das PDF-
 * Rendering (html2canvas) nimmt den Standalone-String, nicht dieses Dokument —
 * beide bleiben unskaliert (Pixelmaß der Seite).
 */
function _applyPreviewScale(frame) {
  try {
    const idoc = frame && (frame.contentDocument || (frame.contentWindow && frame.contentWindow.document));
    if (!idoc || !idoc.body) return;
    const body = idoc.body;
    // Erst zurücksetzen, dann die echte Seitenbreite messen.
    body.style.transform = '';
    body.style.width = '';
    idoc.documentElement.style.height = '';
    const pageW = Math.max(idoc.documentElement.scrollWidth, body.scrollWidth, 1);
    const factor = previewScaleFactor(frame.clientWidth, pageW);
    if (factor >= 1) return;
    body.style.width = pageW + 'px';
    body.style.transformOrigin = 'top left';
    body.style.transform = `scale(${factor})`;
    idoc.documentElement.style.height = Math.ceil(body.scrollHeight * factor) + 'px';
    idoc.documentElement.style.overflowX = 'hidden';
  } catch (_) { /* Vorschau bleibt unskaliert */ }
}

/** „Plan ansehen" — Standalone-HTML im iframe (sandbox, kein Style-Leak), Drucken
 *  über das iframe-Fenster. */
export async function openSchedulePreview(overview, opts) {
  closeSchedulePreview();
  const o = overview || {};
  const version = (opts && opts.version) || (o.week && o.week.published_version) || null;
  const label = `Dienstplan KW ${isoWeekNumber(o.week_start)}` + (version ? ` · v${version}` : ' · Vorschau');
  let html = buildStandaloneScheduleHtml(o, { version: version || ((o.week && o.week.published_version) || 0) + 1 });
  try { html = await inlineBrandLogoForEmail(html); } catch (_) { /* Logo optional */ }
  // K4 — Druck bleibt unskaliert: die Bildschirm-Skalierung (inline transform am
  // body) wird im Druckmedium zurückgesetzt. Nur für die Vorschau; das
  // Standalone-HTML fürs PDF bleibt ohne diese Regel.
  html = html.replace('</style>', '  @media print { body { transform: none !important; width: auto !important; } html { height: auto !important; } }\n</style>');

  const modal = document.createElement('div');
  modal.className = 'order-modal-overlay bon-pdf-overlay';
  modal.id = 'sp-preview-modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.innerHTML = `
    <div class="bon-pdf-modal" onclick="event.stopPropagation()">
      <header class="bon-pdf-head">
        <div class="bon-pdf-title">🗓️ ${htmlEscape(label)}</div>
        <div class="bon-pdf-actions">
          <button type="button" class="btn btn-ghost btn-sm" onclick="spPrintPreview()">🖨 Drucken</button>
          <button type="button" class="order-modal-close" onclick="spClosePreview()" aria-label="Schließen">×</button>
        </div>
      </header>
      <div class="bon-pdf-body" id="sp-preview-body"></div>
    </div>`;
  modal.addEventListener('click', () => closeSchedulePreview());
  document.body.appendChild(modal);
  const body = modal.querySelector('#sp-preview-body');
  const frame = document.createElement('iframe');
  frame.className = 'bon-pdf-frame';
  frame.title = label;
  // S230/S254: same-origin für print(), keine Skripte im Standalone-Dokument.
  frame.setAttribute('sandbox', 'allow-same-origin allow-modals');
  frame.addEventListener('load', () => _applyPreviewScale(frame));
  frame.srcdoc = html;
  body.appendChild(frame);
  _previewModal = modal;
  _previewFrame = frame;
  _previewResize = () => _applyPreviewScale(frame);
  window.addEventListener('resize', _previewResize);
  _previewKey = (e) => { if (e.key === 'Escape') closeSchedulePreview(); };
  document.addEventListener('keydown', _previewKey);
}

export function printSchedulePreview() {
  const frame = _previewModal && _previewModal.querySelector('iframe');
  try {
    if (frame && frame.contentWindow) { frame.contentWindow.focus(); frame.contentWindow.print(); }
  } catch (e) { showToast('Drucken nicht möglich: ' + (e.message || e), 'error'); }
}

export function closeSchedulePreview() {
  // K4/Aufräumen: iframe explizit leeren + entfernen (kein verwaistes Dokument),
  // Listener abhängen, Modal weg.
  if (_previewFrame) {
    try { _previewFrame.removeAttribute('srcdoc'); _previewFrame.remove(); } catch (_) {}
    _previewFrame = null;
  }
  if (_previewResize) { try { window.removeEventListener('resize', _previewResize); } catch (_) {} _previewResize = null; }
  if (_previewKey) { try { document.removeEventListener('keydown', _previewKey); } catch (_) {} _previewKey = null; }
  if (_previewModal) { try { _previewModal.remove(); } catch (_) {} _previewModal = null; }
}

/** „PDF öffnen" — das gespeicherte PDF der letzten Version im Bon-PDF-Modal. */
export function openSchedulePdf(overview) {
  const o = overview || {};
  const week = o.week;
  if (!week || !week.pdf_path) { showToast('Noch kein PDF — zuerst veröffentlichen.', 'info'); return; }
  const sp = window.App && window.App.storage && window.App.storage.schedulePdfs;
  if (typeof window.openPdfByPath !== 'function' || !sp || typeof sp.downloadPdfBlob !== 'function') {
    showToast('PDF-Anzeige nicht verfügbar.', 'error'); return;
  }
  const fn = schedulePdfFilename(o.week_start, week.published_version);
  window.openPdfByPath(week.pdf_path, `Dienstplan KW ${isoWeekNumber(o.week_start)} · v${week.published_version || 1}`, fn, (p) => sp.downloadPdfBlob(p),
    { noun: 'Dienstplan-PDF' });
}

if (typeof window !== 'undefined') {
  Object.assign(window, { spPrintPreview: printSchedulePreview, spClosePreview: closeSchedulePreview });
}
